// 🔹 Assets
import { exerciseImages } from 'src/assets/images/exercises';

export type MuscleGroup = 'Lower Body' | 'Upper Body' | 'Full Body' | 'Core';
export type ExerciseType = 'Warmup' | 'Main' | 'Cooldown' | 'Fitness' | 'Cardio';
export type ExerciseMetric = 'reps' | 'duration';

export interface Exercise {
  _id: string;
  name: string;
  muscleGroup: MuscleGroup;
  type: ExerciseType[];
  thumbnail: string;
  sets: number;
  reps: number | string;
  duration?: string;
  metricType: ExerciseMetric;
  supportedMetrics: ExerciseMetric[];
  rest: string;
  impact: string;
}

export const exercise_list = [
  'Squat',
  'Bicep Curl',
  'Shoulder Press',
  'Press Up',
  'Side Lunge',
  'Jumping Jacks',
  'Plank',
  'Knee To Elbow',
  'High Knees',
  'Jabs',
  'Wood Cutter Left to Right',
  'Wood Cutter Right to Left',
  'Upper Cut',
  'Dumbbell Lateral Raise',
  'Squat Jumps',
  'Tricep Dips-Bench',
  'Wall Sit',
  'Burpees',
];

export const PLACEHOLDER_EXERCISE_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%231f2233"/><text x="50%" y="50%" fill="%23fafafa" font-family="sans-serif" font-size="12" text-anchor="middle" dy=".3em">Exercise</text></svg>';

// 🔹 Dynamic Image Matching
const normalizeExerciseName = (name: string) => {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace('moutain', 'mountain')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

export const getClosestExerciseImage = (name: string): string => {
  if (!name) return PLACEHOLDER_EXERCISE_IMAGE;
  const normalized = normalizeExerciseName(name);

  // Exact match
  if (exerciseImages[normalized]) {
    return exerciseImages[normalized];
  }

  // Fuzzy match
  const keys = Object.keys(exerciseImages);
  for (const key of keys) {
    if (key.includes(normalized) || normalized.includes(key)) {
      return exerciseImages[key];
    }
  }

  return PLACEHOLDER_EXERCISE_IMAGE;
};

// 🔹 Generate unique ID from name
const generateId = (name: string, index: number): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') // replace spaces & special chars
    .replace(/^_|_$/g, '') + `_${index + 1}`;

// 🔹 Determine muscle group
export const getMuscleGroup = (name: string): MuscleGroup => {
  const lowerName = name.toLowerCase().replace('moutain', 'mountain');
  if (/(squat|lunge|leg|calf|glute|thrust|hamstring)/.test(lowerName)) return 'Lower Body';
  if (/(plank|core|crunch|twist|wood|knee to elbow|dead bug|bird dog)/.test(lowerName))
    return 'Core';
  if (/(burpee|jack|mountain climber|jump rope|rowing|cycling|high knee)/.test(lowerName))
    return 'Full Body';
  return 'Upper Body'; // default
};

// 🔹 Determine exercise type
export const getExerciseType = (name: string): ExerciseType[] => {
  const lowerName = name.toLowerCase().replace('moutain', 'mountain');
  const types: ExerciseType[] = [];
  if (/(jack|jump rope|cycling|rowing)/.test(lowerName)) types.push('Warmup');
  if (/(burpee|jump|high knee|jab|upper cut|mountain climber|cardio)/.test(lowerName))
    types.push('Cardio');
  if (/(wall sit|stretch|cool)/.test(lowerName)) types.push('Cooldown');

  if (types.length === 0) {
    types.push('Main', 'Fitness');
  } else if (!types.includes('Cooldown')) {
    types.push('Fitness');
  }
  return types;
};

// 🔹 Determine whether an exercise uses reps, duration, or lets the trainer choose one.
export const getSupportedMetrics = (name: string): ExerciseMetric[] => {
  const lowerName = name.toLowerCase().replace('moutain', 'mountain');
  if (/(plank|wall sit|stretch|hold)/.test(lowerName)) return ['duration'];
  if (
    /(curl|press|raise|row|extension|fly|crush|dip|squat|lunge|deadlift|thrust|bridge)/.test(
      lowerName,
    )
  )
    return ['reps'];
  return ['reps', 'duration'];
};

export const getDefaultMetric = (name: string): ExerciseMetric => {
  const metrics = getSupportedMetrics(name);
  return metrics.includes('reps') ? 'reps' : 'duration';
};

// 🔹 Determine reps and duration defaults dynamically
export const getReps = (name: string): number | string => {
  if (!getSupportedMetrics(name).includes('reps')) return '';
  return 12;
};

export const getDuration = (name: string): string => {
  if (!getSupportedMetrics(name).includes('duration')) return '';
  const lowerName = name.toLowerCase().replace('moutain', 'mountain');
  if (/(plank|wall sit|jack|high knee|jab|upper cut|rope|climb|rowing|cycling)/.test(lowerName))
    return '30s';
  return '';
};

// 🔹 Determine impact
export const getImpact = (name: string): string => {
  const lowerName = name.toLowerCase().replace('moutain', 'mountain');
  if (/(burpee|high knee|jack|jump|plyo|swing|rope|climber)/.test(lowerName)) return 'High imp.';
  if (/(plank|wall sit|raise|curl|extension|fly|sit-up|crunch)/.test(lowerName)) return 'Low imp.';
  return 'Medium imp.';
};

// 🔹 Generate mock data
export const mockExercises: Exercise[] = exercise_list.map((name, index) => ({
  _id: generateId(name, index),
  name,
  muscleGroup: getMuscleGroup(name),
  type: getExerciseType(name),
  thumbnail: getClosestExerciseImage(name),
  sets: 3,
  reps: getReps(name),
  duration: getDuration(name),
  metricType: getDefaultMetric(name),
  supportedMetrics: getSupportedMetrics(name),
  rest: '45s rest',
  impact: getImpact(name),
}));
